import type {
  Appointment,
  CodeableConcept,
  Extension,
  HealthcareService,
} from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from './namespace';

/**
 * Eluve-authored display scheduling semantics on HealthcareService: minutes
 * live as valueInteger sub-extensions of the scheduling-parameters extension,
 * and the optional classification is a `type` coding on the appointment-type
 * system (a scheduling label, never a billing code).
 *
 * Distinct from the Medplum engine input (`scheduling.ts`'s
 * MEDPLUM_SCHEDULING_PARAMETERS_EXTENSION): this extension is what the
 * fhir-renderer reads/writes for display and editing.
 *
 * The `/fhir` segment in these systems is not the namespace grammar's — it
 * predates it and is already stored on live resources, so it stays until a
 * migration moves it.
 */

export const SCHEDULING_PARAMETERS_EXTENSION = `${ELUVE_FHIR_NAMESPACE}/fhir/StructureDefinition/scheduling-parameters`;

export const APPOINTMENT_TYPE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/fhir/CodeSystem/appointment-type`;

/**
 * The eluve appointment row has no service column, so the chosen
 * HealthcareService lives on the FHIR Appointment's serviceType, keyed by
 * this system. Header reads and the create dialog write must agree.
 */
export const APPOINTMENT_SERVICE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/fhir/healthcare-service`;

/** serviceType coding for a chosen service, or undefined to clear it. */
export function serviceTypeForService(
  service: HealthcareService | undefined,
): CodeableConcept[] | undefined {
  if (!service?.id) return undefined;
  return [
    {
      coding: [
        {
          system: APPOINTMENT_SERVICE_SYSTEM,
          code: service.id,
          display: service.name,
        },
      ],
    },
  ];
}

/** The selected service id on an appointment, if any. */
export function selectedServiceCode(
  appointment: Appointment | null | undefined,
): string | undefined {
  return appointment?.serviceType?.[0]?.coding?.find(
    (coding) => coding.system === APPOINTMENT_SERVICE_SYSTEM,
  )?.code;
}

export const APPOINTMENT_TYPE_LABELS: Record<string, string> = {
  'new-patient': 'First visit',
  returning: 'Follow-up',
};

export type SchedulingParameters = {
  durationMinutes?: number;
  alignmentIntervalMinutes?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
};

const PARAMETER_NAMES = [
  'durationMinutes',
  'alignmentIntervalMinutes',
  'bufferBeforeMinutes',
  'bufferAfterMinutes',
] as const;

export function schedulingParameters(
  data: HealthcareService,
): SchedulingParameters {
  const block = data.extension?.find(
    (extension) => extension.url === SCHEDULING_PARAMETERS_EXTENSION,
  );
  const parameters: SchedulingParameters = {};
  for (const name of PARAMETER_NAMES) {
    const entry = block?.extension?.find((extension) => extension.url === name);
    const value = entry?.valueInteger ?? entry?.valuePositiveInt;
    if (value !== undefined) parameters[name] = value;
  }
  return parameters;
}

/** Rewrites only the scheduling-parameters block; foreign extensions survive. */
export function withSchedulingParameters(
  data: HealthcareService,
  parameters: SchedulingParameters,
): HealthcareService {
  const entries: Extension[] = PARAMETER_NAMES.flatMap((name) => {
    const value = parameters[name];
    return value === undefined ? [] : [{ url: name, valueInteger: value }];
  });
  const foreign = (data.extension ?? []).filter(
    (extension) => extension.url !== SCHEDULING_PARAMETERS_EXTENSION,
  );
  const extension =
    entries.length === 0
      ? foreign
      : [
          ...foreign,
          { url: SCHEDULING_PARAMETERS_EXTENSION, extension: entries },
        ];
  return { ...data, extension: extension.length === 0 ? undefined : extension };
}

export function appointmentTypeCode(
  data: HealthcareService,
): string | undefined {
  return data.type
    ?.flatMap((concept) => concept.coding ?? [])
    .find((coding) => coding.system === APPOINTMENT_TYPE_SYSTEM)?.code;
}

export function schedulingSummary(
  parameters: SchedulingParameters,
): string | undefined {
  const parts: string[] = [];
  if (parameters.durationMinutes !== undefined) {
    parts.push(`${parameters.durationMinutes} min`);
  }
  if (parameters.alignmentIntervalMinutes !== undefined) {
    parts.push(`${parameters.alignmentIntervalMinutes} min spacing`);
  }
  if (parameters.bufferBeforeMinutes !== undefined) {
    parts.push(`${parameters.bufferBeforeMinutes} min buffer before`);
  }
  if (parameters.bufferAfterMinutes !== undefined) {
    parts.push(`${parameters.bufferAfterMinutes} min buffer after`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}
